// Data hooks for the public status page (TOM-930). The page refetches every
// 60 s while it is visible, and at once when it becomes visible again.

import { useCallback, useEffect, useState } from 'react';

import { api } from '../api';
import type { BoatLink, Incident, IncidentWithUpdates, StatusPage } from './statusPage';

export type Loaded<T> = { kind: 'loading' } | { kind: 'ready'; data: T } | { kind: 'error' };

/** How often a visible status page refetches. */
export const STATUS_REFRESH_MS = 60_000;

/** Load `path` now, then every minute while the tab is visible. */
function usePolled<T>(path: string | null): Loaded<T> {
  const [state, setState] = useState<Loaded<T>>({ kind: 'loading' });

  const load = useCallback(
    async (signal: AbortSignal) => {
      if (path === null) return;
      try {
        const data = await api<T>(path, { signal });
        if (!signal.aborted) setState({ kind: 'ready', data });
      } catch {
        // Keep the last good answer on screen; show an error only without one.
        if (!signal.aborted) setState((s) => (s.kind === 'ready' ? s : { kind: 'error' }));
      }
    },
    [path]
  );

  useEffect(() => {
    if (path === null) return;
    const controller = new AbortController();
    setState({ kind: 'loading' });
    void load(controller.signal);
    const tick = () => {
      if (document.visibilityState === 'visible') void load(controller.signal);
    };
    const timer = window.setInterval(tick, STATUS_REFRESH_MS);
    document.addEventListener('visibilitychange', tick);
    return () => {
      controller.abort();
      window.clearInterval(timer);
      document.removeEventListener('visibilitychange', tick);
    };
  }, [path, load]);

  return state;
}

export function useStatusPage(): Loaded<StatusPage> {
  return usePolled<StatusPage>('/v1/status');
}

/** The signed-in owner's boats. Mount only for a signed-in user. */
export function useStatusBoats(): Loaded<{ boats: BoatLink[] }> {
  return usePolled<{ boats: BoatLink[] }>('/v1/status/boats');
}

/** One month's incidents for the calendar; null skips the fetch. */
export function useIncidentMonth(month: string | null): Loaded<{ incidents: Incident[] }> {
  const [state, setState] = useState<Loaded<{ incidents: Incident[] }>>({ kind: 'loading' });
  useEffect(() => {
    if (month === null) return;
    const controller = new AbortController();
    setState({ kind: 'loading' });
    void (async () => {
      try {
        const data = await api<{ incidents: Incident[] }>(
          `/v1/status/incidents?month=${encodeURIComponent(month)}`,
          { signal: controller.signal }
        );
        if (!controller.signal.aborted) setState({ kind: 'ready', data });
      } catch {
        if (!controller.signal.aborted) setState({ kind: 'error' });
      }
    })();
    return () => controller.abort();
  }, [month]);
  return state;
}

export function useIncident(
  id: string | undefined
): Loaded<IncidentWithUpdates> | { kind: 'missing' } {
  const [state, setState] = useState<Loaded<IncidentWithUpdates> | { kind: 'missing' }>({
    kind: 'loading',
  });
  useEffect(() => {
    if (id === undefined) return;
    const controller = new AbortController();
    setState({ kind: 'loading' });
    void (async () => {
      try {
        const data = await api<{ incident: IncidentWithUpdates }>(
          `/v1/status/incidents/${encodeURIComponent(id)}`,
          { signal: controller.signal }
        );
        if (!controller.signal.aborted) setState({ kind: 'ready', data: data.incident });
      } catch (err) {
        if (controller.signal.aborted) return;
        setState(
          (err as { status?: number }).status === 404 ? { kind: 'missing' } : { kind: 'error' }
        );
      }
    })();
    return () => controller.abort();
  }, [id]);
  return state;
}
